import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import { TextAlignCenter } from 'lucide-react';
import { useNavigate } from "react-router-dom";
// --- Configuration ---

const API_BASE =
    process.env.REACT_APP_DOCUMENT_LINK;
const PAGE_SIZE = 100;

interface TableInfo {
    text: string;
    value: string; // "001ARD^^Key1,Key2"
}

interface ColumnSchema {
    name: string;
    type: string;
    length: number;
    nullable: boolean;
}

const EL_GeneralARD: React.FC = () => {
    // --- State ---
    const [tables, setTables] = useState<TableInfo[]>([]);
    const [selectedTableValue, setSelectedTableValue] = useState('');
    const [selectedTableName, setSelectedTableName] = useState('');

    const [schema, setSchema] = useState<ColumnSchema[]>([]);
    const [data, setData] = useState<any[]>([]);
    const [keyFields, setKeyFields] = useState('');
    const [rights, setRights] = useState(''); // 'ARD' string

    const [whereClause, setWhereClause] = useState('');
    const [orderByClause, setOrderByClause] = useState('');

    const [viewMode, setViewMode] = useState<'List' | 'Entry'>('List');
    const [entryData, setEntryData] = useState<any>({});
    const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);

    // Pagination State (Visual Only for UI matching)
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showNulls, setShowNulls] = useState(false);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    // --- Styles ---
    const styles = {
        container: { fontFamily: 'Arial', fontSize: '10pt', margin: '5px', backgroundColor: '#fff', minHeight: '100vh' },

        // Top Query Section
        queryBox: {
            border: '1px solid #80a0ff',
            borderBottom: 'none',
            backgroundColor: '#EBE9FD',
            padding: '5px'
        },
        table: { width: '100%', fontFamily: 'Arial', fontSize: '10pt', borderCollapse: 'collapse' as const },
        tdLabel: { width: '100px', padding: '2px', whiteSpace: 'nowrap' as const },
        tdInput: { padding: '2px' },

        textBox: {
            width: '1200px',
            border: '1px solid #003300',
            fontSize: '10pt',
            paddingLeft: '5px',
            height: '20px'
        },
        ddl: { width: '1200px', fontFamily: 'Arial', fontSize: '10pt', height: "20px" },

        // Buttons (Blue with Orange Border - Matches Screenshot)
        btnBlueOrange: {
            backgroundColor: '#119ECC',
            fontSize: '10pt',
            fontWeight: 'bold',
            color: 'White',
            border: '2px solid orange',
            height: '26px',
            width: '80px',
            cursor: 'pointer',
            marginRight: '5px',
            marginLeft: '5px'
        },

        // Grid Container
        gridContainer: {
            width: '100%',
            overflow: 'auto',
            height: 'calc(100vh - 300px)',
            border: '1px solid #80a0ff',
            borderTop: 'none'
        },

        // The specific "Contents of table" header bar
        gridTitleBar: {
            backgroundColor: '#c7ddfd',
            color: '#2859ac',
            textAlign: 'center' as const,
            fontWeight: 'bold',
            fontSize: '10pt',
            padding: '10px',
            border: '1px solid #80a0ff',
            borderBottom: 'none'
        },

        // Table Styles
        gridTable: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            fontSize: '10pt', // Requested 10pt

        },

        // Column Headers
        gridHeader: {
            backgroundColor: '#c7ddfd', // Requested Heading BG
            color: '#2859ac',           // Requested Heading Font Color
            fontWeight: 'normal' as const,
            padding: '10px',
            border: '1px solid #80a0ff', // Requested Border Color
            position: 'sticky' as const,
            top: 0,
            whiteSpace: 'nowrap' as const,
            textAlign: 'left' as const
        },

        // Cells
        gridCell: {
            borderRight: '1px solid #80a0ff', // Requested Border Color
            padding: '4px 4px',
            whiteSpace: 'nowrap' as const,
            color: 'inherit',
        },
        gridCellInput: { width: '100%', border: 'none', backgroundColor: 'transparent', color: 'inherit', fontSize: '10pt' },

        // Row Colors
        rowEven: { backgroundColor: '#ffffff', color: '#000' },
        rowOdd: { backgroundColor: '#f4faff', color: '#000' }, // Requested Odd Row BG
        rowSelected: { backgroundColor: '#ffa500', color: '#fff' }, // Requested OnClick Colors

        // Footer / Pagination Bar
        footerBar: {
            backgroundColor: '#EBE9FD',
            border: '1px solid #80a0ff',
            borderTop: 'none',
            padding: '5px',
            textAlign: 'center' as const,
            fontSize: '10pt',
            color: 'blue'
        },
        footerLink: {
            textDecoration: 'none',
            color: 'blue',
            fontWeight: 'bold',
            margin: '0 3px',
            cursor: 'pointer'
        },

        // Bottom Action Bar
        bottomActionBar: {
            textAlign: 'center' as const,
            padding: '10px',
            backgroundColor: '#fff'
        },

        // Entry Form
        entryContainer: { width: '450px', margin: '20px auto', border: '1px solid #80a0ff', padding: '0px' },
        entryHeader: { backgroundColor: '#2f6089', color: 'white', padding: '5px', fontWeight: 'bold' },
        entryRow: { borderBottom: '1px solid #80a0ff' },
        entryLabel: { width: '150px', backgroundColor: '#EFF3FB', padding: '5px', borderRight: '1px solid #80a0ff', fontSize: '10pt' },
        entryInputCell: { padding: '5px', backgroundColor: 'white' },
        entryInput: { width: '100%', border: '1px solid #ccc', fontSize: '10pt' }
    };

    document.body.style.backgroundColor = "#fff"
    // --- Init ---
    useEffect(() => {
        const fetchTables = async () => {
            try {
                const res = await fetch(`${API_BASE}api/general-ard-tables/`);
                const json = await res.json();
                setTables(json.tables);
            } catch (err) {
                console.error(err);
            }
        };
        fetchTables();
    }, []);

    // --- Handlers ---

    const handleTableChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value;
        setSelectedTableValue(val);

        if (val) {
            const parts = val.split('^^');
            console.log("Selected Table Value:", val, "Split Parts:", parts);
            const meta = parts[0];
            console.log("Selected Table Value:", val, "Meta:", meta);
            const keys = parts[1];
            console.log("Selected Table Meta:", meta, "Key Fields:", keys);
            const rightsStr = meta.substring(3); // ARD
            console.log("Selected Table Meta:", meta, "Rights:", rightsStr);
            setRights(rightsStr);
            const cleanedKeys = keys
                ?.split(',')
                .map(k => k.trim().toUpperCase())
                .filter(k => k !== '')
                .join(',');

            setKeyFields(cleanedKeys);

            const tbl = tables.find(t => t.value === val);
            if (tbl) setSelectedTableName(tbl.text);
        } else {
            setRights('');
            setKeyFields('');
            setSelectedTableName('');
        }

        setData([]);
        setSchema([]);
        setEditingRowIndex(-1);
        setTotalRecords(0);
    };

    const handleDisplay = async () => {
        if (!selectedTableName) {
            alert("Select a table first");
            return;
        }
        setLoading(true);
        try {
            const url =
                `${API_BASE}api/general-ard-data/?` +
                `TableName=${selectedTableName}` +
                `&Where=${whereClause}` +
                `&OrderBy=${orderByClause}` +
                `&Page=${currentPage}` +
                `&PageSize=${PAGE_SIZE}`;

            const res = await fetch(url);
            const json = await res.json();

            if (json.error) {
                setError(json.error);
            } else {
                setSchema(json.schema);
                setData(json.data);
                setTotalRecords(json.totalRecords);
                setTotalPages(Math.ceil(json.totalRecords / PAGE_SIZE) || 1);

                setViewMode('List');
                setEditingRowIndex(-1);
            }
        } catch (err) {
            setError('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const goFirst = () => setCurrentPage(1);

    const goPrev = () => {
        if (currentPage > 1) setCurrentPage(p => p - 1);
    };

    const goNext = () => {
        if (currentPage < totalPages) setCurrentPage(p => p + 1);
    };

    const goLast = () => setCurrentPage(totalPages);

    useEffect(() => {
        if (selectedTableName) {
            handleDisplay();
        }
    }, [currentPage]);

    const handleAdd = () => {
        const emptyEntry: any = {};
        schema.forEach(col => emptyEntry[col.name] = '');
        setEntryData(emptyEntry);
        setViewMode('Entry');
    };

    const handleEntrySave = async () => {
        try {
            const payload = {
                Action: 'A',
                TableName: selectedTableName,
                RowData: entryData,
                KeyFields: keyFields
            };
            const res = await fetch(`${API_BASE}api/general-ard-action/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const json = await res.json();
            if (json.error) {
                alert(json.error);
            } else {
                alert('Record added successfully');
                setViewMode('List');
                handleDisplay();
            }
        } catch (err) {
            alert('Save failed');
        }
    };

    const handleModify = async () => {
        if (editingRowIndex === -1) {
            alert("Select a row first");
            return;
        }

        const row = data[editingRowIndex];

        try {
            // 🔥 1. Normalize RowData keys to UPPERCASE
            const normalizedRow: any = {};
            Object.keys(row).forEach((k) => {
                normalizedRow[k.toUpperCase()] = row[k];
            });

            // 🔥 2. Normalize KeyFields to UPPERCASE
            const cleanKeyFields = keyFields
                ?.split(',')
                .map(k => k.trim().toUpperCase())
                .filter(k => k !== '')
                .join(',');

            const payload = {
                Action: 'R',
                TableName: selectedTableName,
                RowData: normalizedRow,
                KeyFields: cleanKeyFields
            };

            // 🔍 DEBUG (keep for now)
            console.log("FINAL PAYLOAD:", payload);
            console.log("KEYFIELDS:", cleanKeyFields);
            console.log("ROW KEYS:", Object.keys(normalizedRow));

            const res = await fetch(`${API_BASE}api/general-ard-action/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                alert('Row updated successfully');
                setEditingRowIndex(-1);
                handleDisplay();
            }

        } catch (err) {
            console.error(err);
            alert('Update failed');
        }
    };

    const handleDelete = async () => {
        if (editingRowIndex === -1) {
            alert("Select a row first");
            return;
        }

        if (!window.confirm("Do you really want to delete the selected row?")) return;

        try {
            const row = data[editingRowIndex];

            // 🔥 1. Normalize RowData keys to UPPERCASE
            const normalizedRow: any = {};
            Object.keys(row).forEach((k) => {
                normalizedRow[k.toUpperCase()] = row[k];
            });

            // 🔥 2. Normalize KeyFields to UPPERCASE
            const cleanKeyFields = keyFields
                ?.split(',')
                .map(k => k.trim().toUpperCase())
                .filter(k => k !== '')
                .join(',');

            const payload = {
                Action: 'D',
                TableName: selectedTableName,
                RowData: normalizedRow,
                KeyFields: cleanKeyFields
            };

            // 🔍 DEBUG (keep for now)
            console.log("FINAL DELETE PAYLOAD:", payload);
            console.log("KEYFIELDS:", cleanKeyFields);
            console.log("ROW KEYS:", Object.keys(normalizedRow));

            const res = await fetch(`${API_BASE}api/general-ard-action/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const json = await res.json();

            console.log("RESPONSE:", json);

            if (json.error) {
                alert(json.error);
            } else {
                alert('Deleted successfully');
                setEditingRowIndex(-1);
                handleDisplay();
            }

        } catch (err) {
            console.error(err);
            alert('Delete failed');
        }
    };


    const formatDate = (value: any): string => {
        if (value === null || value === undefined || value === "") {
            return "";
        }

        // Handle values like:
        // 2015-05-04
        // 2015-05-04T00:00:00
        // 2015-05-04T00:00:00.000
        const valueString = String(value);

        const datePart = valueString.substring(0, 10);

        // If it is YYYY-MM-DD, format it directly.
        // This avoids timezone issues.
        const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);

        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }

        // Fallback for other valid date formats
        const date = new Date(value);

        if (isNaN(date.getTime())) {
            return valueString;
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };


    const formatValue = (
        value: any,
        column: ColumnSchema
    ): string => {

        if (value === null || value === undefined || value === "") {
            return "";
        }

        const columnName = column.name
            .replace(/\s/g, "")
            .toLowerCase();

        // Date columns
        if (
            columnName === "registereddate" ||
            columnName === "expirydate" ||
            columnName === "loggedon" ||
            columnName === "loggeddate"
        ) {
            return formatDate(value);
        }

        return String(value);
    };


    const formatCellValue = (
        value: any,
        columnName: string
    ): string => {

        if (value === null || value === undefined || value === "") {
            return "";
        }

        const normalizedName = columnName
            .replace(/\s/g, "")
            .toLowerCase();

        if (
            normalizedName === "registereddate" ||
            normalizedName === "expirydate" ||
            normalizedName === "loggedon" ||
            normalizedName === "loggeddate"
        ) {
            return formatDate(value);
        }

        return String(value);
    };

    const handleCellChange = (rowIndex: number, colName: string, val: string) => {
        const newData = [...data];
        newData[rowIndex][colName] = val;
        setData(newData);
    };

    // --- Render ---

    if (viewMode === 'Entry') {
        return (
            <div style={styles.container}>
                <div style={styles.entryContainer}>
                    <div style={styles.gridTitleBar}>Data entry for table : {selectedTableName}</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            {schema.map(col => (
                                <tr key={col.name} style={styles.entryRow}>
                                    <td style={styles.entryLabel}>{col.name}</td>
                                    <td style={styles.entryInputCell}>
                                        <input
                                            type="text"
                                            value={entryData[col.name] || ''}
                                            onChange={(e) => setEntryData({ ...entryData, [col.name]: e.target.value })}
                                            style={styles.entryInput}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#EBE9FD' }}>
                        <button style={styles.btnBlueOrange} onClick={handleEntrySave}>Save</button>
                        <button style={styles.btnBlueOrange} onClick={() => setViewMode('List')}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container} id="divMain">
            {loading && <Loading />}
            {/* Query Box */}
            <div id="divQueries" style={{ ...styles.queryBox, margin: '10px' }}>
                <table style={{ ...styles.table, padding: "20px" }}>
                    <tbody>
                        <tr>
                            <td style={{ ...styles.tdLabel, fontSize: "11pt" }}>Select Table:</td>
                            <td style={styles.tdInput}>
                                <select style={styles.ddl} onChange={handleTableChange} value={selectedTableValue}>
                                    <option value="">-- Select --</option>
                                    {tables.map(t => <option key={t.value} value={t.value}>{t.text}</option>)}
                                </select>
                                &nbsp;&nbsp;&nbsp;&nbsp;
                                <button style={styles.btnBlueOrange} onClick={handleDisplay}>Display</button>
                                <button style={styles.btnBlueOrange} onClick={() => navigate("/SysAdmin")}>Go Back</button>
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...styles.tdLabel, fontSize: "11pt" }}>Key Fields:</td>
                            <td style={styles.tdInput}>
                                <span style={{ fontFamily: 'Trebuchet MS', fontSize: '11pt', color: 'black' }}>{keyFields}</span>
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>WHERE:</td>
                            <td style={styles.tdInput}>
                                <input type="text" style={styles.textBox} value={whereClause} onChange={e => setWhereClause(e.target.value)} />
                            </td>
                        </tr>
                        <tr>
                            <td style={styles.tdLabel}>ORDER BY:</td>
                            <td style={styles.tdInput}>
                                <input type="text" style={styles.textBox} value={orderByClause} onChange={e => setOrderByClause(e.target.value)} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Data Grid Section */}
            {schema.length > 0 && (
                <>
                    <div style={styles.gridTitleBar}>Contents of table : {selectedTableName}</div>
                    <div style={styles.gridContainer}>
                        <table style={styles.gridTable}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.gridHeader, width: '30px', textAlign: 'center' }}>#</th>
                                    {schema.map(col => (
                                        <th key={col.name} style={styles.gridHeader}>{col.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, rIdx) => {
                                    const isEditing = editingRowIndex === rIdx;
                                    const isOdd = (rIdx + 1) % 2 !== 0;

                                    // Row style priority: Selected > Odd > Even
                                    let rowStyle = isOdd ? styles.rowOdd : styles.rowEven;

                                    if (isEditing) {
                                        rowStyle = styles.rowSelected;
                                    }

                                    return (
                                        <tr
                                            key={rIdx}
                                            style={{ ...rowStyle, cursor: 'pointer' }}
                                            onDoubleClick={() => setEditingRowIndex(rIdx)}
                                            onClick={() => setEditingRowIndex(rIdx)}
                                        >
                                            <td style={{ ...styles.gridCell, textAlign: 'center' }}>
                                                {(currentPage - 1) * 100 + rIdx + 1}
                                            </td>

                                            {schema.map(col => (
                                                <td key={col.name} style={{ ...styles.gridCell, height: "30px", fontSize: "10pt", borderLeft: "1px solid blue" }}>
                                                    {isEditing ? (
                                                        <input
                                                            type="text"
                                                            value={
                                                                row[col.name] !== null && row[col.name] !== undefined
                                                                    ? formatCellValue(row[col.name], col.name)
                                                                    : "NULL"
                                                            }
                                                            onChange={(e) =>
                                                                handleCellChange(rIdx, col.name, e.target.value)
                                                            }
                                                            style={styles.gridCellInput}
                                                        />
                                                    ) : (
                                                        <span>
                                                            {formatCellValue(row[col.name], col.name)}
                                                        </span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination Footer */}
                    <div style={styles.footerBar}>
                        <input
                            type="checkbox"
                            id="chkShowNulls"
                            checked={showNulls}
                            onChange={e => setShowNulls(e.target.checked)}
                        />
                        <label htmlFor="chkShowNulls" style={{ marginRight: '15px' }}>Show Nulls.</label>

                        <span style={styles.footerLink} onClick={goFirst}>&lt;&lt;</span>
                        <span style={styles.footerLink} onClick={goPrev}>&lt;</span>

                        <span style={{ margin: '0 5px', color: 'blue' }}>
                            {currentPage} of {totalPages}
                        </span>

                        <span style={styles.footerLink} onClick={goNext}>&gt;</span>
                        <span style={styles.footerLink} onClick={goLast}>&gt;&gt;</span>

                    </div>
                </>
            )}

            <div style={styles.bottomActionBar}>
                {rights.includes('A') && (
                    <button style={styles.btnBlueOrange} onClick={handleAdd}>Add</button>
                )}
                {rights.includes('R') && (
                    <button style={styles.btnBlueOrange} onClick={handleModify}>Modify</button>
                )}
                {rights.includes('D') && (
                    <button style={styles.btnBlueOrange} onClick={handleDelete}>Delete</button>
                )}
                <button style={styles.btnBlueOrange} onClick={() => { setData([]); setSchema([]); }}>Clear</button>
            </div>
        </div>
    );
};

export default EL_GeneralARD;